import React from "react";
import {
  X as XIcon, Inbox, Activity, ShieldCheck, Bug, Database,
  CheckCircle2, AlertCircle, XCircle, ArrowRight, Play,
} from "lucide-react";

/**
 * IntegrationHealthTutorial — overlay modal that explains every section of
 * the /admin/integration-health page. Aimed at coordinators and on-call
 * engineers who haven't seen the page before.
 *
 * Mounted via a "?" pill in the IntegrationHealth header. Persists
 * `wi-pa-health-tutorial-seen` in localStorage so repeat visits skip the
 * "first-time hint" pulse on the help pill.
 */

function PillSwatch({ status, label }) {
  const colors = {
    green: "bg-[#DCFCE7] text-[#166534] border-[#BBF7D0]",
    yellow: "bg-[#FEF3C7] text-[#854D0E] border-[#FDE68A]",
    red: "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]",
    gray: "bg-[#F3F4F6] text-[#4B5563] border-[#E5E7EB]",
  };
  const icons = {
    green: <CheckCircle2 className="w-3 h-3" />,
    yellow: <AlertCircle className="w-3 h-3" />,
    red: <XCircle className="w-3 h-3" />,
    gray: <AlertCircle className="w-3 h-3" />,
  };
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${colors[status]}`}
    >
      {icons[status]} {label}
    </span>
  );
}

function Section({ icon: Icon, title, children, testId }) {
  return (
    <section className="mb-7" data-testid={testId}>
      <h3 className="font-display text-[16px] font-bold text-[#0B1E3A] mb-2 flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-[#0A3D79]" />}
        {title}
      </h3>
      <div className="text-[13px] leading-relaxed text-[#3A5275] space-y-2">
        {children}
      </div>
    </section>
  );
}

export default function IntegrationHealthTutorial({ open, onClose, onStartTour }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="health-tutorial-modal"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between gap-4 px-6 py-4 border-b border-[#D6E3EF]">
          <div>
            <div className="text-[11px] uppercase tracking-[0.16em] text-[#3A5275] font-semibold">
              Tutorial
            </div>
            <h2 className="font-display text-[20px] font-bold text-[#0B1E3A] mt-1">
              How to read this page
            </h2>
            <p className="text-[12px] text-[#3A5275] mt-1 max-w-xl">
              Five sections, three pill colors, one rule of thumb. Read once
              and you'll know whether the integration is healthy at a glance.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[#3A5275] hover:text-[#0B1E3A] p-1 rounded-md"
            data-testid="health-tutorial-close"
            aria-label="Close tutorial"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          <Section title="Rule of thumb — what the colors mean" testId="tutorial-colors">
            <div className="space-y-2">
              <div className="flex items-start gap-3">
                <PillSwatch status="green" label="Green" />
                <div>
                  <strong className="text-[#0B1E3A]">All good.</strong>{" "}
                  The thing it represents is configured and either working or has
                  no recent failures. Nothing for you to do.
                </div>
              </div>
              <div className="flex items-start gap-3">
                <PillSwatch status="yellow" label="Yellow" />
                <div>
                  <strong className="text-[#0B1E3A]">Heads-up, not broken.</strong>{" "}
                  Something is intentional-but-noteworthy — e.g. demo mode is on,
                  or the URL points back at this same pod (loopback). Safe for
                  preview; flip off before production.
                </div>
              </div>
              <div className="flex items-start gap-3">
                <PillSwatch status="red" label="Red" />
                <div>
                  <strong className="text-[#0B1E3A]">Action needed.</strong>{" "}
                  A required env var is empty or a recent webhook failed. Check
                  the matching card in <em>Last events</em> — it tells you which
                  draft and what error.
                </div>
              </div>
              <div className="flex items-start gap-3">
                <PillSwatch status="gray" label="Gray" />
                <div>
                  <strong className="text-[#0B1E3A]">No data yet.</strong>{" "}
                  Nothing has been recorded. Common when the integration just
                  went live and no traffic has flowed yet. Not a problem.
                </div>
              </div>
            </div>
          </Section>

          <Section icon={Database} title="1. Top banner — Integration mode" testId="tutorial-mode">
            <p>
              Big <strong>PRODUCTION</strong> or <strong>PREVIEW</strong> label
              + 6 status pills.
            </p>
            <ul className="list-disc ml-5 space-y-1.5">
              <li>
                <strong className="text-[#0B1E3A]">PRODUCTION</strong> means at
                least one real secret (Avida or Parent JWT) is set — the strict
                JWT guard is active and standalone-intake CTAs are hidden on the
                Landing page.
              </li>
              <li>
                <strong className="text-[#0B1E3A]">PREVIEW</strong> means
                everything is in sandbox / loopback mode. Secrets are empty;
                JWT guards are bypassed. Don't deploy in this state.
              </li>
              <li>
                The 6 pills track:
                <ul className="list-disc ml-5 mt-1 space-y-1">
                  <li>
                    <strong>Secrets</strong> — is the JWT shared secret set?
                    (red = won't be able to verify Avida-issued RN tokens)
                  </li>
                  <li>
                    <strong>Avida URL</strong> — is `AVIDA_CARE_URL` set to a
                    real Avida pod? (yellow = loopback to ourselves; red = empty)
                  </li>
                  <li>
                    <strong>SMTP</strong> — Gmail App Password configured?
                    (red = email alerts won't deliver, just no-op)
                  </li>
                  <li>
                    <strong>Slack</strong> — `SLACK_DIGEST_WEBHOOK_URL` set?
                    (red = daily digest + real-time alerts no-op)
                  </li>
                  <li>
                    <strong>Sentry</strong> — error tracking DSN set?
                    (red = exceptions go to logs only; not to the Sentry dashboard)
                  </li>
                  <li>
                    <strong>Mock active</strong> — `ENABLE_AVIDA_MOCK=true`?
                    (yellow = demo-seed button works, but flip to false for
                    production)
                  </li>
                </ul>
              </li>
            </ul>
          </Section>

          <Section icon={Activity} title="2. Last events — 4 cards" testId="tutorial-last-events">
            <p>
              Each card answers <em>"when did this last work?"</em> If it's
              green with a recent timestamp, the corresponding integration leg
              is healthy.
            </p>
            <div className="space-y-2.5 mt-2">
              <div className="rounded-lg border border-[#D6E3EF] bg-[#F9FAFB] p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Inbox className="w-4 h-4 text-[#3A5275]" />
                  <strong className="text-[#0B1E3A] text-[14px]">
                    Avida → us · seed-draft
                  </strong>
                </div>
                <div>
                  Last time Avida POSTed a coordinator-built visit assignment to
                  us. Shows the pipeline_id + the RN it was locked to. If green,
                  Avida's outbound webhook is firing and our HMAC verification
                  is accepting it.
                </div>
              </div>
              <div className="rounded-lg border border-[#D6E3EF] bg-[#F9FAFB] p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Activity className="w-4 h-4 text-[#3A5275]" />
                  <strong className="text-[#0B1E3A] text-[14px]">
                    Us → Avida · completion ACK
                  </strong>
                </div>
                <div>
                  Last time the wizard finished generating PDFs and we POSTed
                  the completion bundle to Avida — and Avida ACKd 200. Green
                  here means PDFs reached the Avida EHR.
                </div>
              </div>
              <div className="rounded-lg border border-[#D6E3EF] bg-[#F9FAFB] p-3">
                <div className="flex items-center gap-2 mb-1">
                  <ShieldCheck className="w-4 h-4 text-[#3A5275]" />
                  <strong className="text-[#0B1E3A] text-[14px]">
                    Us → Avida · pa-outcome ACK
                  </strong>
                </div>
                <div>
                  Last time an admin recorded a PA outcome (approved/denied) and
                  we delivered the regenerated documents to Avida. Same as
                  above but for the post-PA leg.
                </div>
              </div>
              <div className="rounded-lg border border-[#D6E3EF] bg-[#F9FAFB] p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Bug className="w-4 h-4 text-[#3A5275]" />
                  <strong className="text-[#0B1E3A] text-[14px]">
                    Last failure
                  </strong>
                </div>
                <div>
                  Most recent webhook failure across either leg. Green means no
                  failures in the last 90 days. Red means a recent retry-loop
                  exhaustion — click into the PA Outcomes inbox to manually
                  resend, OR check Sentry for the stack trace.
                </div>
              </div>
            </div>
          </Section>

          <Section title="3. Live counters — 6 tiles" testId="tutorial-counters">
            <p>Real-time inventory of in-flight + stuck work.</p>
            <ul className="list-disc ml-5 space-y-1.5">
              <li>
                <strong className="text-[#0B1E3A]">In-flight</strong> — drafts
                whose completion webhook is currently mid-retry. Should be near
                zero except during active intake bursts.
              </li>
              <li>
                <strong className="text-[#0B1E3A]">Sync failed</strong> —{" "}
                <em>red-accent when {`>`} 0</em>. These exhausted all retries to
                Avida. Coordinator must manually click <em>Resend</em> in the PA
                Outcomes inbox once the upstream issue is fixed.
              </li>
              <li>
                <strong className="text-[#0B1E3A]">Standalone pending</strong>{" "}
                — orphan intakes (RN started one without an Avida seed) waiting
                for an admin to <em>Reconcile</em> them to a real Avida pipeline.
              </li>
              <li>
                <strong className="text-[#0B1E3A]">PA pending</strong> —
                drafts that finished the wizard but no admin has recorded the
                ForwardHealth PA decision yet. Action: visit{" "}
                <code className="text-[11px] bg-gray-100 px-1 rounded">
                  /drafts/pending
                </code>{" "}
                and record approve/deny.
              </li>
              <li>
                <strong className="text-[#0B1E3A]">Agencies</strong> — total
                organizations registered.
              </li>
              <li>
                <strong className="text-[#0B1E3A]">With recipients</strong> —
                how many agencies have at least one alert email set. If this is
                less than <em>Agencies</em>, the missing ones will fall back to{" "}
                <code className="text-[11px] bg-gray-100 px-1 rounded">
                  EMAIL_ALERT_FALLBACK_RECIPIENTS
                </code>
                . Visit <em>PA Outcomes inbox → Recipients</em> to fix.
              </li>
            </ul>
          </Section>

          <Section icon={Database} title="4. Configuration card" testId="tutorial-config">
            <p>
              Read-only values from <code className="text-[11px] bg-gray-100 px-1 rounded">backend/.env</code>.
              Use this when something looks wrong — confirm the URL hasn't typo'd
              and the configured flags match what you expect.
            </p>
            <ul className="list-disc ml-5 space-y-1">
              <li>
                <strong>Loopback mode</strong> = true means we're talking to
                ourselves (preview demo). Flip{" "}
                <code className="text-[11px] bg-gray-100 px-1 rounded">AVIDA_CARE_URL</code>{" "}
                to a real Avida pod for production.
              </li>
              <li>
                <strong>Mock enabled</strong> = true means the demo-seed button
                on Landing works. Set{" "}
                <code className="text-[11px] bg-gray-100 px-1 rounded">ENABLE_AVIDA_MOCK=false</code>{" "}
                in production.
              </li>
            </ul>
          </Section>

          <Section title="5. Auto-refresh + Refresh button" testId="tutorial-refresh">
            <p>
              The page polls every 5s when <em>Auto-refresh</em> is checked.
              The backend caches the response for 5s server-side so polling
              doesn't hammer Mongo. Hit <em>Refresh</em> to force-skip the
              cache.
            </p>
          </Section>

          <Section title="When to act — quick checklist" testId="tutorial-checklist">
            <ul className="list-disc ml-5 space-y-1.5">
              <li>
                <strong className="text-[#991B1B]">Sync failed {">"} 0</strong>{" "}
                → open <code className="text-[11px] bg-gray-100 px-1 rounded">/drafts/pa-outcomes</code>,
                find the rows with red badges, click <em>Resend</em>.
              </li>
              <li>
                <strong className="text-[#991B1B]">Last failure card red</strong>{" "}
                → check the error message; common causes are 401 (clock skew or
                wrong secret) and 5xx from Avida (their pod might be down).
                Sentry has the stack trace.
              </li>
              <li>
                <strong className="text-[#991B1B]">Any red pill in the banner</strong>{" "}
                → an env var is empty. Drop the missing value into{" "}
                <code className="text-[11px] bg-gray-100 px-1 rounded">backend/.env</code>{" "}
                and restart the backend.
              </li>
              <li>
                <strong className="text-[#854D0E]">Yellow pills</strong> → these
                are fine for preview/demo. Flip them green before deploying to
                production by replacing loopback URLs with real ones and
                disabling the mock.
              </li>
            </ul>
          </Section>
        </div>

        <footer className="px-6 py-3 border-t border-[#D6E3EF] bg-[#F7FAFD] flex items-center justify-between gap-4">
          <span className="text-[11px] text-[#3A5275]">
            Bookmark this page — it's the fastest pre-deploy sanity check.
          </span>
          <div className="flex items-center gap-2">
            {onStartTour && (
              <button
                type="button"
                onClick={() => {
                  onClose();
                  // Slight delay so the modal close animation completes
                  // before the tour spotlight starts measuring elements.
                  setTimeout(() => onStartTour(), 200);
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#9333EA] text-white text-[12px] font-semibold hover:bg-[#7E22CE]"
                data-testid="health-tutorial-start-tour"
              >
                <Play className="w-3.5 h-3.5" />
                Start guided tour
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="btn-ghost"
              data-testid="health-tutorial-done"
            >
              Got it <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
